import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Newspaper, Sparkles, Check, X, Pencil, Trash2, ExternalLink, RefreshCw, Link as LinkIcon } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const STATUS_STYLES = {
  draft: "bg-[#F6F1E7] text-[#8A6D2F] border-[#D4B36A]/50",
  published: "bg-[#EAF2EC] text-[#2D6A4F] border-[#2D6A4F]/30",
  rejected: "bg-[#FBEAEA] text-[#B42318] border-[#B42318]/30",
};

function EditDialog({ post, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (post) {
      setForm({ title: post.title, summary: post.summary, journal: post.journal || "",
                year: post.year || "", source_url: post.source_url || "", peptide: post.peptide || "" });
    }
  }, [post]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/bulletin/${post.id}`, {
        ...form, year: form.year ? parseInt(form.year, 10) : null,
      });
      toast.success("Post updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update post");
    } finally {
      setSaving(false);
    }
  };

  if (!post || !form) return null;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="bulletin-edit-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wide">Edit Bulletin Post</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#6E675F] block mb-1">Title</label>
            <Input data-testid="bulletin-edit-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-[#6E675F] block mb-1">Summary</label>
            <Textarea data-testid="bulletin-edit-summary" rows={4} value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs text-[#6E675F] block mb-1">Journal</label>
              <Input data-testid="bulletin-edit-journal" value={form.journal} onChange={(e) => setForm((f) => ({ ...f, journal: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-[#6E675F] block mb-1">Year</label>
              <Input data-testid="bulletin-edit-year" type="number" value={form.year} onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#6E675F] block mb-1">Study link (PubMed / DOI)</label>
            <Input data-testid="bulletin-edit-url" value={form.source_url} onChange={(e) => setForm((f) => ({ ...f, source_url: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-[#6E675F] block mb-1">Peptide</label>
            <Input data-testid="bulletin-edit-peptide" value={form.peptide} onChange={(e) => setForm((f) => ({ ...f, peptide: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="bulletin-edit-save" onClick={save} disabled={saving} className="bg-[#8C7B6B] text-white hover:bg-[#75665A]">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PostCard({ p, onAction, onEdit }) {
  return (
    <div className="border border-[#DDD7CF] rounded-lg bg-white p-5" data-testid={`bulletin-post-${p.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <span className={`inline-flex text-[10px] font-bold uppercase tracking-wide border rounded-none px-2.5 py-1 ${STATUS_STYLES[p.status]}`} data-testid={`bulletin-status-${p.id}`}>
          {p.status}
        </span>
        {p.peptide && (
          <span className="inline-flex text-[10px] font-semibold uppercase tracking-wide bg-[#EFEBE6] text-[#6E675F] rounded-none px-2.5 py-1">
            {p.peptide}
          </span>
        )}
        <span className="text-xs text-[#6E675F] ml-auto">{new Date(p.created_at).toLocaleDateString("en-CA")}</span>
      </div>
      <p className="font-semibold text-sm text-[#1A1A1A] mt-3">{p.title}</p>
      <p className="text-sm text-[#6E675F] mt-1.5 leading-relaxed">{p.summary}</p>
      <p className="text-xs text-[#6E675F] mt-2">
        {p.journal}{p.year ? ` · ${p.year}` : ""}
        {p.source_url && (
          <a href={p.source_url} target="_blank" rel="noopener noreferrer" className="text-[#8C7B6B] underline ml-2 inline-flex items-center gap-0.5" data-testid={`bulletin-source-${p.id}`}>
            View study <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </p>
      <div className="flex gap-2 mt-4">
        {p.status !== "published" && (
          <Button size="sm" data-testid={`bulletin-approve-${p.id}`} onClick={() => onAction(p, "approve")} className="bg-[#2D6A4F] text-white hover:bg-[#245741]">
            <Check className="w-4 h-4 mr-1" /> Approve & Publish
          </Button>
        )}
        {p.status !== "rejected" && (
          <Button size="sm" variant="outline" data-testid={`bulletin-reject-${p.id}`} onClick={() => onAction(p, "reject")} className="text-[#B42318] border-red-200 hover:bg-red-50">
            <X className="w-4 h-4 mr-1" /> {p.status === "published" ? "Unpublish" : "Reject"}
          </Button>
        )}
        <Button size="sm" variant="outline" data-testid={`bulletin-edit-${p.id}`} onClick={() => onEdit(p)}>
          <Pencil className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="outline" data-testid={`bulletin-delete-${p.id}`} onClick={() => onAction(p, "delete")} className="text-[#B42318] border-red-200 hover:bg-red-50 ml-auto">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export const AdminBulletin = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("draft");
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [addingLink, setAddingLink] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/bulletin")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load bulletin posts"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/bulletin/generate");
      toast.success(data.created > 0
        ? `${data.created} new draft post(s) ready for review`
        : "No new studies found — try again later");
      load();
      if (data.created > 0) setFilter("draft");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const addFromLink = async () => {
    const url = linkUrl.trim();
    if (!url) return;
    setAddingLink(true);
    try {
      await api.post("/admin/bulletin/from-link", { url });
      toast.success("Draft created from your link — review it below");
      setLinkUrl("");
      setFilter("draft");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create a post from that link");
    } finally {
      setAddingLink(false);
    }
  };

  const action = async (p, kind) => {
    try {
      if (kind === "delete") {
        if (!window.confirm(`Delete "${p.title.slice(0, 60)}"?`)) return;
        await api.delete(`/admin/bulletin/${p.id}`);
        toast.success("Post deleted");
      } else {
        await api.post(`/admin/bulletin/${p.id}/${kind}`);
        toast.success(kind === "approve" ? "Post published to the homepage bulletin" : "Post rejected");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  if (!data) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { posts, counts } = data;
  const visible = filter === "all" ? posts : posts.filter((p) => p.status === filter);
  const filters = [
    { key: "draft", label: `Pending Review (${counts.draft})` },
    { key: "published", label: `Published (${counts.published})` },
    { key: "rejected", label: `Rejected (${counts.rejected})` },
    { key: "all", label: "All" },
  ];

  return (
    <div data-testid="admin-bulletin-section">
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-2">
          <Newspaper className="w-4 h-4" /> Research Bulletin
        </p>
        <div className="flex items-center gap-3">
          <button onClick={load} data-testid="bulletin-refresh-button" className="text-xs text-[#8C7B6B] font-semibold inline-flex items-center gap-1 hover:text-[#1A1A1A] transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <Button data-testid="bulletin-generate-button" onClick={generate} disabled={generating} className="bg-[#8C7B6B] text-white hover:bg-[#75665A]">
            <Sparkles className="w-4 h-4 mr-1.5" /> {generating ? "Harvesting studies…" : "Generate Posts"}
          </Button>
        </div>
      </div>

      <p className="text-xs text-[#6E675F] mb-6 max-w-2xl">
        AI harvests published academic studies about peptides each week and drops them here as drafts.
        Nothing appears on the Research page until you approve it. You can edit any post before publishing.
      </p>

      <div className="border border-[#DDD7CF] rounded-lg bg-white p-4 mb-6 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="bulletin-link-form">
        <div className="flex items-center gap-2 shrink-0">
          <LinkIcon className="w-4 h-4 text-[#8C7B6B]" />
          <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E675F]">Add your own study</span>
        </div>
        <Input
          data-testid="bulletin-link-input"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addFromLink()}
          placeholder="Paste a study link (PubMed, DOI, journal…) — AI fills in the rest"
          className="flex-1"
        />
        <Button data-testid="bulletin-link-submit" onClick={addFromLink} disabled={addingLink || !linkUrl.trim()} className="bg-[#8C7B6B] text-white hover:bg-[#75665A]">
          {addingLink ? "Reading study…" : "Fetch & Add"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map((f) => (
          <button
            key={f.key}
            data-testid={`bulletin-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`text-xs tracking-[0.1em] uppercase font-semibold px-4 py-2 rounded-none border transition-colors ${
              filter === f.key ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "text-[#6E675F] border-[#DDD7CF] hover:border-[#8C7B6B]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-[#6E675F] py-10 text-center border border-[#DDD7CF] rounded-lg bg-white" data-testid="bulletin-empty">
          {filter === "draft" ? "No drafts waiting — click Generate Posts to harvest new studies." : `No ${filter === "all" ? "" : filter} posts.`}
        </p>
      ) : (
        <div className="space-y-4">
          {visible.map((p) => (
            <PostCard key={p.id} p={p} onAction={action} onEdit={setEditing} />
          ))}
        </div>
      )}

      <EditDialog post={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
};
